/**
 * Stateless helpers for weekly scoring (shared by weekScoreEngine and ScoreService).
 */

/**
 * Normalize contestant identifier to a display name.
 * Map is contestantId -> name. Ids resolve to names; values that are
 * already display names (legacy drafts and week results) are returned as-is.
 * @param {string} contestant - Contestant ID or name
 * @param {Object|null} contestantsMap - Season map contestantId -> name
 * @returns {string}
 */
export function normalizeContestantToName(contestant, contestantsMap = null) {
  if (!contestant) return '';
  if (contestantsMap && contestantsMap[contestant]) {
    return contestantsMap[contestant];
  }
  return contestant;
}

/**
 * @param {Object} results - Week results
 * @param {Object|null} contestantsMap - Season map contestantId -> name
 * @returns {string[]}
 */
export function getVotedOutNames(results, contestantsMap = null) {
  if (!results || !results.votedOut) return [];

  if (Array.isArray(results.votedOut)) {
    if (results.votedOut.length > 0 && results.votedOut[0].position) {
      return results.votedOut.map(v =>
        normalizeContestantToName(
          v.contestantName || v.contestantId,
          contestantsMap
        )
      );
    }
    return results.votedOut.map(v =>
      normalizeContestantToName(v, contestantsMap)
    );
  }
  return [normalizeContestantToName(results.votedOut, contestantsMap)];
}

/**
 * @param {Object} results
 * @returns {boolean}
 */
export function isOrderedEliminationResults(results) {
  return (
    Array.isArray(results?.votedOut) &&
    results.votedOut.length > 0 &&
    results.votedOut[0]?.position != null
  );
}

/**
 * @param {Object} results
 * @returns {Array}
 */
export function getOrderedEliminationRows(results) {
  if (!isOrderedEliminationResults(results)) return [];
  return [...results.votedOut].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  );
}

/**
 * @param {Object} row
 * @param {Object|null} seasonContestantsMap - contestantId -> name
 * @returns {string|null}
 */
export function getResultRowContestantId(row, seasonContestantsMap) {
  if (!row) return null;
  if (row.contestantId) return row.contestantId;
  if (row.contestantName && seasonContestantsMap) {
    return (
      Object.keys(seasonContestantsMap).find(
        id => seasonContestantsMap[id] === row.contestantName
      ) || null
    );
  }
  return null;
}

/**
 * @param {string} contestantName
 * @param {Object} seasonData
 * @param {number} weekNumber
 * @param {Object|null} contestantsMap
 * @returns {boolean}
 */
export function wasContestantEliminatedPreviously(
  contestantName,
  seasonData,
  weekNumber,
  contestantsMap = null
) {
  if (!seasonData || !seasonData.weeks) return false;

  const sortedWeeks = [...seasonData.weeks].sort(
    (a, b) => a.weekNumber - b.weekNumber
  );

  for (const week of sortedWeeks) {
    if (week.weekNumber >= weekNumber) break;

    if (week.results && week.results.votedOut) {
      const previousEliminated = getVotedOutNames(week.results, contestantsMap);
      if (previousEliminated.includes(contestantName)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @param {Object} results
 * @returns {Array}
 */
export function getVotedOutContestants(results) {
  if (!results || !results.votedOut) return [];

  if (Array.isArray(results.votedOut)) {
    if (results.votedOut.length > 0 && results.votedOut[0].position) {
      return results.votedOut.map(
        item => item.contestantName || item.contestantId
      );
    }
    return results.votedOut;
  }
  return [results.votedOut];
}
